import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert, AutoComplete, Button, Col, ConfigProvider, DatePicker, Descriptions, Dropdown, Empty, Form, Input,
  InputNumber, Modal, Popconfirm, Row, Select, Space, Spin, Switch, Table,
  Tag, Timeline, Typography, Upload, message,
} from "antd";
import {
  CheckCircleOutlined, ClockCircleOutlined, DollarOutlined,
  FileAddOutlined, MenuOutlined, PlusOutlined, ReloadOutlined,
  SafetyCertificateOutlined, SearchOutlined, UploadOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import http from "@/utils/http";
import api from "@/utils/api";
import commonFn from "@/utils/commonFn";
import MoveInModal from "./MoveInModal";
import HistoryCheckModal from "./HistoryCheckModal";
import TransferTenantModal from "./TransferTenantModal";
import ChangeUnitModal from "./ChangeUnitModal";
import { historyIdentity, hasCurrentHistoryReview } from "./historyReview";
import "./index.css";

const { Text } = Typography;
const STATUS_OPTIONS = [
  { label: "All statuses", value: "" },
  { label: "Pending", value: "Pending" },
  { label: "Available", value: "Available" },
  { label: "Approved", value: "Approved" },
  { label: "Rejected", value: "Rejected" },
  { label: "Rejected.E", value: "Rejected.E" },
];
const SUPERVISOR_STATUS_OPTIONS = STATUS_OPTIONS.slice(1).filter((option) => option.value !== "Rejected.E");
const money = (amount) => new Intl.NumberFormat("en-US", {
  style: "currency", currency: "USD",
}).format(Number(amount || 0));
const feeAmount = (amount, currency = false) => new Intl.NumberFormat("en-US", {
  style: currency ? "currency" : "decimal", currency: "USD",
  minimumFractionDigits: 0, maximumFractionDigits: 2,
}).format(Number(amount || 0));
const must = (result) => {
  if (result?.code !== "1") throw new Error(result?.msg || "Request failed");
  return result.data;
};
const badge = (status) => {
  const color = status === "Approved" || status === "Available" ? "green"
    : status === "Pending" ? "gold" : status?.startsWith("Rejected") ? "red" : "default";
  return <Tag color={color}>{status || "—"}</Tag>;
};
const paymentName = (code) => ({ 101: "Paid", 102: "Skipped", 201: "Unpaid", 202: "Partial" }[code] || "—");
const statusLabel = (text, tone) => <span className={`cc-decision cc-decision-${tone}`}>
  <span className="cc-decision-dot" />{text || "—"}
</span>;
const PAYMENT_OPTIONS = [
  { label: "Paid", value: "101" }, { label: "Skipped", value: "102" },
  { label: "Unpaid", value: "201" }, { label: "Partial", value: "202" },
];
const CREDIT_OPTIONS = [{ label: "Yes", value: "1" }, { label: "No", value: "0" }];
const RENTAL_ASSISTANCE_OPTIONS = [{ label: "Nothing selected", value: "" },
  { label: "No", value: "0" }, { label: "Yes", value: "1" }];
const SUFFIX_OPTIONS = ["Jr", "Sr", "I", "II", "III", "IV"].map((value) => ({ label: value, value }));
const FILTER_FIELDS = [
  "groupCode", "prop", "unit", "tenant", "unitAddress", "city", "state", "rent", "deposit",
  "applicationId", "applicant", "ssn", "runCredit", "creditDate", "applicationStatus",
  "supervisorStatus", "paymentStatus", "moveInDate",
  "orderedBy", "ranBy",
];
const applicantName = (person) => [person.firstName, person.middleName, person.lastName].filter(Boolean).join(" ");
const displayDate = (value) =>
  value && !String(value).startsWith("1000-") ? dayjs(value).format("MM/DD/YYYY") : "—";
const formatReportXml = (xml) => xml.replace(/>\s*</g, ">\n<");
const newApplicant = () => ({ runCredit: true });
const ssnLast4 = (value) => String(value || "").replace(/\D/g, "").slice(-4) || "—";
const matchLocation = (input, option) => String(option?.label || option?.value || "")
  .toLowerCase().includes(input.toLowerCase());
const locationOptions = (options) => (options || []).map(({ label, value }) => ({
  label: String(label || "").trim(), value: String(value || "").trim(),
})).filter(({ value }) => value);

export default function CreditCheckIndex() {
  const [form] = Form.useForm();
  const requestId = useRef(null);
  const createInFlight = useRef(false);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [filters, setFilters] = useState({ page: 1, size: 25 });
  const [filterDraft, setFilterDraft] = useState({});
  const listRequest = useRef(0);
  const summaryRequest = useRef(0);
  const detailRequest = useRef(0);
  const propertyRequest = useRef(0);
  const unitRequest = useRef(0);
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [openingDetailId, setOpeningDetailId] = useState(null);
  const [actionLoadingId, setActionLoadingId] = useState(null);
  const [changeUnitApplication, setChangeUnitApplication] = useState(null);
  const [changingUnitId, setChangingUnitId] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [unitQuoteLoading, setUnitQuoteLoading] = useState(false);
  const [uploadType, setUploadType] = useState(null);
  const uploadInFlight = useRef(false);
  const [openingFileId, setOpeningFileId] = useState(null);
  const fileInFlight = useRef(false);
  const reportRequest = useRef(0);
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [historySsns, setHistorySsns] = useState(null);
  const [reviewedHistory, setReviewedHistory] = useState(null);
  const reviewedHistoryRef = useRef(null);
  const historySnapshot = useRef(null);
  const [createFiles, setCreateFiles] = useState([]);
  const [pendingUploads, setPendingUploads] = useState([]);
  const [uploadingApplicationId, setUploadingApplicationId] = useState(null);
  const [propertyOptions, setPropertyOptions] = useState([]);
  const [propertyLoading, setPropertyLoading] = useState(false);
  const [unitOptions, setUnitOptions] = useState([]);
  const [unitLoading, setUnitLoading] = useState(false);
  const [unitQuote, setUnitQuote] = useState(null);
  const [feePolicy, setFeePolicy] = useState(null);
  const [formOptions, setFormOptions] = useState({ orderedBy: [], assistingEntities: [], creditCheckSupervisor: "" });
  const [cityOptions, setCityOptions] = useState([]);
  const [cityOptionsByState, setCityOptionsByState] = useState({});
  const [cityLoadErrors, setCityLoadErrors] = useState({});
  const loadingCityStates = useRef(new Set());
  const [stateOptions, setStateOptions] = useState([]);
  const watchedApplicants = Form.useWatch("applicants", form);
  const historyReady = hasCurrentHistoryReview(reviewedHistory, watchedApplicants);
  useEffect(() => {
    if (!hasCurrentHistoryReview(reviewedHistoryRef.current, watchedApplicants)) {
      reviewedHistoryRef.current = null;
      setReviewedHistory(null);
    }
  }, [watchedApplicants]);
  const creditApplicantCount = (watchedApplicants || []).filter((person) => person?.runCredit !== false).length;
  const selectedPropId = Form.useWatch("propId", form);
  const selectedUnitId = Form.useWatch("unitId", form);
  const rentalAssistance = Form.useWatch("section8", form);
  const [moveInOpen, setMoveInOpen] = useState(false);
  const [transferApplication, setTransferApplication] = useState(null);
  const [moveInApplication, setMoveInApplication] = useState(null);
  const [moveInTargetId, setMoveInTargetId] = useState(null);
  const [adjustTarget, setAdjustTarget] = useState(null);
  const [amount, setAmount] = useState(0);
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);
  const [busyAction, setBusyAction] = useState(null);
  const [report, setReport] = useState(null);
  const can = (action) => commonFn.btnAuthorityFlag(action);

  const loadSummary = useCallback(async () => {
    const request = ++summaryRequest.current;
    setSummaryLoading(true);
    try {
      const data = must(await http.get(api.creditCheckSummary));
      if (request === summaryRequest.current) setSummary(data);
    } catch (_error) {
      if (request === summaryRequest.current) {
        setSummary(null);
        message.error("Could not load application summary");
      }
    } finally {
      if (request === summaryRequest.current) setSummaryLoading(false);
    }
  }, []);

  const loadList = useCallback(async () => {
    const request = ++listRequest.current;
    setLoading(true);
    try {
      const data = must(await http.get(api.creditCheckList, { params: filters }));
      if (request !== listRequest.current) return;
      setRows(data.list || []);
      setTotal(data.total || 0);
    } catch (error) {
      if (request === listRequest.current) message.error(error.message || "Could not load applications");
    } finally {
      if (request === listRequest.current) setLoading(false);
    }
  }, [filters]);

  useEffect(() => { loadList(); }, [loadList]);
  useEffect(() => { loadSummary(); }, [loadSummary]);
  useEffect(() => {
    if (!createOpen || !selectedUnitId) {
      setUnitQuote(null);
      setUnitQuoteLoading(false);
      return undefined;
    }
    let active = true;
    setUnitQuote(null);
    setUnitQuoteLoading(true);
    http.get(api.creditCheckUnitQuote, { params: { unitId: selectedUnitId } })
      .then((result) => { if (active) setUnitQuote(must(result)); })
      .catch(() => { if (active) message.error("Could not load unit rent and deposit"); })
      .finally(() => { if (active) setUnitQuoteLoading(false); });
    return () => { active = false; };
  }, [createOpen, selectedUnitId]);
  useEffect(() => {
    let active = true;
    const loadOptions = async (url, setter) => {
      try {
        const options = must(await http.get(url));
        if (active) setter(locationOptions(options));
      } catch (_error) {
        if (active) message.error("Could not load location choices");
      }
    };
    loadOptions(api.getCityDictByProperty, setCityOptions);
    loadOptions(api.getStateDictByProperty, setStateOptions);
    return () => { active = false; };
  }, []);
  useEffect(() => {
    const timer = setTimeout(() => setFilters((current) => {
      const changed = FILTER_FIELDS.some((field) => (current[field] || "") !== (filterDraft[field]?.trim() || ""));
      if (!changed) return current;
      const active = Object.fromEntries(FILTER_FIELDS.filter((field) => filterDraft[field]?.trim())
        .map((field) => [field, filterDraft[field].trim()]));
      return { page: 1, size: current.size, bucket: current.bucket, ...active };
    }), 350);
    return () => clearTimeout(timer);
  }, [filterDraft]);

  const loadDetail = async (applicationId, show = true) => {
    const request = ++detailRequest.current;
    setActionLoadingId(applicationId);
    if (show) {
      setOpeningDetailId(applicationId);
      if (detail?.applicationId !== applicationId) setDetail(null);
    }
    setDetailLoading(true);
    try {
      const data = must(await http.get(api.creditCheckDetail, { params: { applicationId } }));
      if (request !== detailRequest.current) return null;
      if (show) setDetail(data);
      return data;
    } catch (error) {
      if (request === detailRequest.current) message.error(error.message || "Could not load application");
      return null;
    } finally {
      if (request === detailRequest.current) {
        setDetailLoading(false);
        setOpeningDetailId(null);
        setActionLoadingId(null);
      }
    }
  };

  const closeDetail = () => {
    detailRequest.current += 1;
    setDetail(null);
    setOpeningDetailId(null);
    setDetailLoading(false);
    setActionLoadingId(null);
  };

  const openChangeUnit = async (applicationId) => {
    setChangingUnitId(applicationId);
    try {
      const application = await loadDetail(applicationId, false);
      if (!application) return;
      if (application.movedInStatus) {
        message.warning("This application has already moved in. Use Transfer tenant instead.");
        return;
      }
      setChangeUnitApplication(application);
    } finally {
      setChangingUnitId(null);
    }
  };

  const refresh = () => {
    loadList();
    loadSummary();
    if (detail?.applicationId) loadDetail(detail.applicationId);
  };

  const searchProperties = async (content = "") => {
    const request = ++propertyRequest.current;
    setPropertyOptions([]);
    setPropertyLoading(true);
    try {
      const data = must(await http.get(api.getPropSearch, { params: { content } }));
      if (request !== propertyRequest.current) return;
      setPropertyOptions((data || []).map((property) => ({
        value: property.propId,
        label: property.propName || property.fullDisplay || property.prop,
      })));
    } catch (_error) {
      if (request === propertyRequest.current) {
        setPropertyOptions([]);
        message.error("Could not load properties");
      }
    } finally {
      if (request === propertyRequest.current) setPropertyLoading(false);
    }
  };

  const searchUnits = async (propId, content = "") => {
    const request = ++unitRequest.current;
    if (!propId) {
      setUnitOptions([]);
      setUnitLoading(false);
      return;
    }
    setUnitOptions([]);
    setUnitLoading(true);
    try {
      const data = must(await http.get(api.unitSearch, { params: { propId, content } }));
      if (request !== unitRequest.current) return;
      setUnitOptions((data || []).map((unit) => ({
        value: unit.unitId,
        label: unit.unitName || unit.unit,
      })));
    } catch (_error) {
      if (request === unitRequest.current) {
        setUnitOptions([]);
        message.error("Could not load units for this property");
      }
    } finally {
      if (request === unitRequest.current) setUnitLoading(false);
    }
  };

  const openCreate = () => {
    setFormLoading(true);
    Promise.all([
      http.get(api.creditCheckFeePolicy).then((result) => setFeePolicy(must(result)))
        .catch(() => setFeePolicy(null)),
      http.get(api.creditCheckFormOptions).then((result) => setFormOptions(must(result)))
        .catch(() => message.error("Could not load application choices")),
    ]).finally(() => setFormLoading(false));
    requestId.current = window.crypto?.randomUUID?.() || "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g,
      (character) => {
        const random = Math.floor(Math.random() * 16);
        return (character === "x" ? random : (random & 3) | 8).toString(16);
      });
    form.resetFields();
    setCreateFiles([]);
    setHistorySsns(null);
    setReviewedHistory(null);
    reviewedHistoryRef.current = null;
    form.setFieldsValue({ applicants: [newApplicant()], pictureDelay: false, manager: false, section8: "" });
    setUnitOptions([]);
    searchProperties();
    setCreateOpen(true);
  };

  const loadCitiesForState = async (state) => {
    if (!state || (cityOptionsByState[state] && !cityLoadErrors[state]) || loadingCityStates.current.has(state)) return;
    loadingCityStates.current.add(state);
    setCityOptionsByState((current) => ({ ...current, [state]: undefined }));
    setCityLoadErrors((current) => ({ ...current, [state]: false }));
    try {
      const options = must(await http.get(api.getCityDictByState, { params: { state } }));
      setCityOptionsByState((current) => ({ ...current, [state]: locationOptions(options) }));
    } catch (_error) {
      message.error("Could not load cities for this state");
      setCityLoadErrors((current) => ({ ...current, [state]: true }));
      setCityOptionsByState((current) => ({ ...current, [state]: [] }));
    } finally {
      loadingCityStates.current.delete(state);
    }
  };

  const checkHistory = async () => {
    try {
      const fields = (form.getFieldValue("applicants") || []).map((_, index) => ["applicants", index, "socialSecurity"]);
      await form.validateFields(fields);
      if (!fields.length) return;
      historySnapshot.current = historyIdentity(form.getFieldValue("applicants"));
      setHistorySsns(form.getFieldValue("applicants").map((person) => person.socialSecurity.replace(/-/g, "")));
    } catch (error) {
      if (error?.errorFields?.length) {
        form.scrollToField(error.errorFields[0].name, { behavior: "smooth", block: "center", focus: true });
      }
    }
  };

  const uploadApplicationFiles = async (applicationId, files) => {
    setUploadingApplicationId(applicationId);
    let failed = 0;
    for (const file of files) {
      const payload = new FormData();
      payload.append("applicationId", applicationId);
      payload.append("type", "application");
      payload.append("file", file.originFileObj || file);
      try {
        const data = must(await http.post(api.creditCheckUpload, payload, {
          headers: { "Content-Type": "multipart/form-data" },
        }));
        setDetail((current) => current?.applicationId === applicationId ? data : current);
        setPendingUploads((current) => current.filter((item) =>
          item.applicationId !== applicationId || item.file.uid !== file.uid));
      } catch (_error) {
        failed += 1;
      }
    }
    setUploadingApplicationId(null);
    if (failed) message.warning("Application saved. Some documents could not be uploaded. Retry them in Documents.");
    else if (files.length) message.success("Application documents uploaded");
  };

  const persistCreate = async (payload) => {
    if (!hasCurrentHistoryReview(reviewedHistoryRef.current, form.getFieldValue("applicants"))
      || !hasCurrentHistoryReview(reviewedHistoryRef.current, payload.applicants)) {
      message.warning("Applicant details changed. Complete History Check again before running credit.");
      return;
    }
    setCreating(true);
    try {
      const data = must(await http.post(api.creditCheckCreate, payload, {
        headers: { "X-Idempotency-Key": requestId.current },
      }));
      setCreateOpen(false);
      setDetail(data);
      message.success("Application submitted");
      loadList();
      loadSummary();
      const files = createFiles.map((file) => file.originFileObj || file);
      setPendingUploads((current) => [...current, ...files.map((file) => ({ applicationId: data.applicationId, file }))]);
      setCreateFiles([]);
      await uploadApplicationFiles(data.applicationId, files);
    } catch (error) {
      message.error(error.message || "Could not save application");
    } finally {
      setCreating(false);
    }
  };

  const submitCreate = async () => {
    if (createInFlight.current) return;
    if (!hasCurrentHistoryReview(reviewedHistoryRef.current, form.getFieldValue("applicants"))) {
      message.warning("Complete History Check before running credit.");
      return;
    }
    createInFlight.current = true;
    try {
      const values = await form.validateFields();
      setCreating(true);
      const applicationValues = { ...values };
      delete applicationValues.propId;
      const payload = {
        ...applicationValues,
        additionalDeposit: values.additionalDeposit || 0,
        applicants: values.applicants.map((person) => ({
          ...person,
          dateOfBirth: person.dateOfBirth?.format("YYYY-MM-DD"),
          runCredit: person.runCredit !== false,
        })),
      };
      const risks = must(await http.post(api.creditCheckPreflight, payload));
      setCreating(false);
      if (risks.length) {
        const blocked = risks.some((risk) =>
          (risk.recentApplication && !can("duplicateCreditCheck")) ||
          (risk.priorEviction && !can("evictedCreditCheck")));
        const description = risks.map((risk) =>
          `Applicant ${risk.applicantIndex + 1} (SSN ••••${risk.ssnLast4}): ${[
            risk.recentApplication ? `credit request in the last ${risk.withinDays} days` : null,
            risk.priorEviction ? "prior eviction" : null,
          ].filter(Boolean).join("; ")}`
        ).join("\n");
        if (blocked) {
          Modal.warning({ title: "History review required", content: <div style={{ whiteSpace: "pre-line" }}>{description}<br />An authorized reviewer must continue.</div> });
        } else {
          Modal.confirm({ title: "Review before ordering credit", content: <div style={{ whiteSpace: "pre-line" }}>{description}<br />Continuing may place a new paid credit order.</div>,
            okText: "Continue and submit", onOk: () => persistCreate(payload) });
        }
      } else {
        await persistCreate(payload);
      }
    } catch (error) {
      if (error?.errorFields?.length) {
        form.scrollToField(error.errorFields[0].name, { behavior: "smooth", block: "center", focus: true });
      } else {
        message.error(error.message || "Could not review applicant history");
      }
    } finally {
      createInFlight.current = false;
      setCreating(false);
    }
  };

  const updateApplication = async (applicationId, url, payload, success) => {
    if (busyRef.current) return false;
    busyRef.current = true;
    setBusy(true);
    setBusyAction({ applicationId, url, kind: payload.kind });
    try {
      const data = must(await http.post(url, { applicationId, ...payload }));
      if (detail?.applicationId === applicationId) setDetail(data);
      message.success(success);
      loadList();
      loadSummary();
      return true;
    } catch (error) {
      message.error(error.message || "Could not update application");
      return false;
    } finally {
      busyRef.current = false;
      setBusy(false);
      setBusyAction(null);
    }
  };

  const updateDetail = (url, payload, success) =>
    updateApplication(detail.applicationId, url, payload, success);

  const openAdjust = (row) => {
    setAdjustTarget(row);
    setAmount(Number(row.receivedAmount || 0));
  };

  const openMoveIn = async (applicationId) => {
    setMoveInTargetId(applicationId);
    try {
      const application = await loadDetail(applicationId, false);
      if (!application) return;
      if (!application.moveInEligibility?.ready || (application.priorEviction && !can("evictionOverride"))) {
        setDetail(application);
        message.warning("Review the remaining move-in requirements in application details");
        return;
      }
      setMoveInApplication(application);
      setMoveInOpen(true);
    } finally {
      setMoveInTargetId(null);
    }
  };

  const decide = (row, kind, value) => {
    const label = kind === "supervisor" ? "Supervisor" : "Application";
    const save = () => updateApplication(row.applicationId, api.creditCheckDecision,
      { kind, value }, `${label} ${value.toLowerCase()}`);
    if (value === "Rejected") {
      Modal.confirm({ title: `Reject ${label.toLowerCase()} #${row.applicationId}?`,
        okText: "Reject", okButtonProps: { danger: true }, onOk: save });
    } else {
      save();
    }
  };

  const statusCell = (row, kind) => {
    const status = kind === "supervisor" ? row.supervisorStatus : row.applicationStatus;
    const tone = status?.includes("Approved") || status === "Available" ? "approved"
      : status?.startsWith("Rejected") ? "rejected" : "pending";
    const label = statusLabel(status, tone);
    if (!can("decision") || row.movedInStatus) return label;
    return <Dropdown trigger={["click"]} menu={{
      items: [
        { key: "Approved", label: "Approve", disabled: status?.includes("Approved") },
        { key: "Rejected", label: "Reject", danger: true, disabled: status?.startsWith("Rejected") },
      ],
      onClick: ({ key }) => decide(row, kind, key),
    }}>
      <Button type="text" className="cc-decision-action" disabled={busy}
        loading={busyAction?.applicationId === row.applicationId && busyAction?.kind === kind}>{label}</Button>
    </Dropdown>;
  };

  const applicantCell = (row, field) => <div className="cc-applicant-lines">
    {!row.applicants?.length && <span>—</span>}
    {(row.applicants || []).map((person, index) => <span key={`${row.applicationId}-${index}`}>
      {field === "name" ? <strong title={applicantName(person)}>{applicantName(person)}</strong>
        : field === "ssn" ? ssnLast4(person.ssnLast4 || person.socialSecurity)
        : person.runCredit && person.runCredit !== "0" ? "Yes" : "No"}
    </span>)}
  </div>;

  const changeFilter = (field, value) => setFilterDraft((current) => ({ ...current, [field]: value || "" }));
  const toggleBucket = (bucket) => setFilters((current) => ({
    ...current, page: 1, bucket: current.bucket === bucket ? undefined : bucket,
  }));
  const filterHeader = (label, field, options, placeholder = "Filter") => <div className="cc-column-filter">
    <span>{label}</span>
    {options ? <Select size="small" allowClear placeholder="All" options={options}
      value={filterDraft[field] || undefined} onChange={(value) => changeFilter(field, value)} />
      : <Input size="small" allowClear placeholder={placeholder} aria-label={`Filter ${field}`}
        value={filterDraft[field] || ""}
        onChange={(event) => changeFilter(field, event.target.value)} />}
  </div>;
  const cityFilterHeader = <div className="cc-column-filter">
    <span>City</span>
    <AutoComplete size="small" allowClear options={cityOptions} value={filterDraft.city || ""}
      onChange={(value) => changeFilter("city", value)} placeholder="" aria-label="Filter city"
      filterOption={matchLocation} />
  </div>;

  const uploadFile = async (file, type, callbacks) => {
    if (uploadInFlight.current) return;
    uploadInFlight.current = true;
    setUploadType(type);
    const payload = new FormData();
    payload.append("applicationId", detail.applicationId);
    payload.append("type", type);
    payload.append("file", file);
    try {
      const data = must(await http.post(api.creditCheckUpload, payload, {
        headers: { "Content-Type": "multipart/form-data" },
      }));
      setDetail(data);
      callbacks?.onSuccess?.(data);
      message.success("File uploaded");
      loadSummary();
    } catch (error) {
      callbacks?.onError?.(error);
      message.error(error.message || "Could not upload file");
    } finally {
      uploadInFlight.current = false;
      setUploadType(null);
    }
  };

  const showFile = async (file) => {
    if (fileInFlight.current) return;
    fileInFlight.current = true;
    setOpeningFileId(file.fileId);
    try {
      const data = must(await http.get(api.creditCheckFile, {
        params: { applicationId: detail.applicationId, fileId: file.fileId },
      }));
      if (data?.status !== 1 || !data?.base64) throw new Error("File could not be opened");
      const blob = await fetch(data.base64).then((response) => response.blob());
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (error) {
      message.error(error.message || "File could not be opened");
    } finally {
      fileInFlight.current = false;
      setOpeningFileId(null);
    }
  };

  const showReport = async (person) => {
    const request = ++reportRequest.current;
    setReport({ name: `${person.firstName} ${person.lastName}`, xml: null });
    try {
      const xml = must(await http.get(api.creditCheckReport, {
        params: { applicationId: detail.applicationId, applicationInfoId: person.applicationInfoId },
      }));
      if (request === reportRequest.current) setReport({ name: `${person.firstName} ${person.lastName}`, xml });
    } catch (error) {
      if (request !== reportRequest.current) return;
      setReport(null);
      message.error(error.message || "Could not open credit report");
    }
  };

  const closeReport = () => { reportRequest.current += 1; setReport(null); };

  const columns = [
    { title: "Action", key: "action", fixed: "left", width: 62, align: "center", render: (_, row) =>
      <Dropdown trigger={["click"]} disabled={busy || detailLoading} menu={{
        items: [
          { key: "details", label: "View details" },
          ...(!row.movedInStatus && can("update") ? [{ key: "changeUnit", label: "Change unit" }] : []),
        ],
        onClick: ({ key }) => key === "changeUnit" ? openChangeUnit(row.applicationId) : loadDetail(row.applicationId),
      }}>
        <Button type="text" className="cc-action-menu" aria-label={`Actions for application ${row.applicationId}`}
          icon={<MenuOutlined />} loading={actionLoadingId === row.applicationId} disabled={busy || detailLoading} />
      </Dropdown> },
    { title: filterHeader("Group", "groupCode", null, ""), dataIndex: "groupCode", width: 65, render: (value) => value || "—" },
    { title: filterHeader("Prop", "prop", null, ""), dataIndex: "prop", width: 59 },
    { title: filterHeader("Unit", "unit", null, ""), dataIndex: "unit", width: 59 },
    { title: filterHeader("Tnt", "tenant", null, ""), dataIndex: "tenantNumber", width: 49, render: (value) => value || "—" },
    { title: filterHeader("Unit Address", "unitAddress", null, ""), dataIndex: "unitAddress", width: 118,
      render: (value) => <span className="cc-address">{value || "—"}</span> },
    { title: cityFilterHeader, dataIndex: "city", width: 86, render: (value) => <span className="cc-city">{value || "—"}</span> },
    { title: filterHeader("State", "state", stateOptions), dataIndex: "state", width: 62, render: (value) => value || "—" },
    { title: filterHeader("Rent", "rent"), dataIndex: "newRent", width: 88, align: "right", render: money },
    { title: filterHeader("Dep.", "deposit"), dataIndex: "deposit", width: 88, align: "right", render: money },
    { title: filterHeader("Application Id", "applicationId", null, ""), dataIndex: "applicationId", width: 97 },
    { title: filterHeader("Applicant", "applicant"), key: "applicants", width: 175, render: (_, row) => applicantCell(row, "name") },
    { title: filterHeader("SSN", "ssn"), key: "ssn", width: 120, render: (_, row) => applicantCell(row, "ssn") },
    { title: filterHeader("Credit?", "runCredit", CREDIT_OPTIONS), key: "runCredit", width: 82, render: (_, row) => applicantCell(row, "credit") },
    { title: filterHeader("Credit Date", "creditDate", null, "YYYY-MM-DD"), dataIndex: "createdAt", width: 115, render: displayDate },
    { title: filterHeader("App. Status", "applicationStatus", STATUS_OPTIONS.slice(1)), key: "applicationStatus", width: 107, render: (_, row) => statusCell(row, "application") },
    { title: filterHeader("Spvr. Status", "supervisorStatus", SUPERVISOR_STATUS_OPTIONS), key: "supervisorStatus", width: 107, render: (_, row) => statusCell(row, "supervisor") },
    { title: "Fee", key: "fee", width: 116, render: (_, row) => <span className="cc-fee-values"
      title={`Received ${money(row.receivedAmount)} / Expected ${money(row.expectedAmount)}`}>
      {can("adjustReceipt") && !row.movedInStatus && Number(row.receivedAmount || 0) > 0
        ? <Button type="link" className="cc-cell-link cc-inline-edit" disabled={busy}
          title="Adjust received amount" onClick={() => openAdjust(row)}>
          {feeAmount(row.receivedAmount, true)}
        </Button> : feeAmount(row.receivedAmount, true)}<span className="cc-fee-divider">/</span>{feeAmount(row.expectedAmount)}
    </span> },
    { title: filterHeader("Pay Status", "paymentStatus", PAYMENT_OPTIONS), key: "paymentStatus", width: 96, render: (_, row) => {
      const tone = row.paymentStatus === 101 ? "approved" : row.paymentStatus === 102 ? "skipped"
        : row.paymentStatus === 202 ? "partial" : "pending";
      const status = statusLabel(paymentName(row.paymentStatus), tone);
      return can("skipPayment") && !row.movedInStatus && [201, 202].includes(row.paymentStatus)
        ? <Popconfirm title={`Skip payment for application #${row.applicationId}?`}
          onConfirm={() => updateApplication(row.applicationId, api.creditCheckSkipPayment, {}, "Payment skipped")}
          okText="Skip payment" disabled={busy}>
          <Button type="text" className="cc-decision-action" disabled={busy} title="Skip payment"
            loading={busyAction?.applicationId === row.applicationId && busyAction?.url === api.creditCheckSkipPayment}>{status}</Button>
        </Popconfirm> : status;
    } },
    { title: filterHeader("Movein Date", "moveInDate", null, "YYYY-MM-DD"), key: "moveInDate", width: 120, render: (_, row) => row.movedInStatus
      ? displayDate(row.moveInDate)
      : "—" },
    { title: filterHeader("Order By", "orderedBy"), dataIndex: "orderedBy", width: 98, render: (value) => value || "—" },
    { title: filterHeader("Run By", "ranBy"), dataIndex: "ranBy", width: 98, render: (value) => value || "—" },
  ];

  const ready = detail?.moveInEligibility?.ready && (!detail?.priorEviction || can("evictionOverride"));
  const unknown = detail?.submissionStates?.some((item) => item.state === "UNKNOWN" || item.state === "SENDING");

  return (
    <ConfigProvider theme={{ token: { fontSize: 15 } }}>
    <div className="cc-page">
      <div className="cc-page-actions">
        <Space>
          <Button icon={<ReloadOutlined />} onClick={refresh} loading={loading || summaryLoading} disabled={busy}>Refresh</Button>
          {can("create") && <Button type="primary" icon={<PlusOutlined />} loading={formLoading} disabled={creating || uploadingApplicationId !== null} onClick={openCreate}>New application</Button>}
        </Space>
      </div>
      <Row gutter={[10, 10]} className="cc-stats">
        {[
          { key: "new", title: "New applications", value: summary?.newSubmittedCount, icon: <ClockCircleOutlined /> },
          { key: "unpaid", title: "Fees outstanding", value: summary?.unsettledFeeCount, icon: <DollarOutlined /> },
          { key: "review", title: "Awaiting approval", value: summary?.awaitingApprovalCount, icon: <SafetyCertificateOutlined /> },
          { key: "complete", title: "Completed move-ins", value: summary?.completedCount, icon: <CheckCircleOutlined /> },
        ].map((item) => <Col key={item.key} xs={12} lg={6}>
          <button type="button" className={`cc-stat-button${filters.bucket === item.key ? " cc-stat-selected" : ""}`}
            aria-pressed={filters.bucket === item.key} onClick={() => toggleBucket(item.key)}>
            <span className="cc-stat-icon">{item.icon}</span>
            <span className="cc-stat-label">{item.title}</span>
            <span className="cc-stat-value">{summaryLoading ? <Spin size="small" /> : item.value?.toLocaleString("en-US") ?? "—"}</span>
          </button>
        </Col>)}
      </Row>

      <Table
          rowClassName="table-row-gradient"
          className="cc-workspace"
          rowKey="applicationId"
          columns={columns}
          dataSource={rows}
          loading={loading}
          size="small"
          locale={{ emptyText: <Empty description="No applications match this view" /> }}
          scroll={{ x: 2020 }}
          pagination={{
            current: filters.page, pageSize: filters.size, total,
            showSizeChanger: true,
            onChange: (page, size) => setFilters((old) => ({ ...old, page, size })),
          }}
      />

      <Modal
        open={createOpen}
        className="cc-create-modal"
        width={1440}
        centered
        title="New application"
        onCancel={() => setCreateOpen(false)}
        maskClosable={false}
        keyboard={!creating}
        closable={!creating}
        destroyOnClose
        footer={<div className="cc-create-footer">
          <div className="cc-create-fee" aria-live="polite">
            <div><span>Estimated credit fee</span><strong>{feePolicy ? money(creditApplicantCount * Number(feePolicy.creditCheckFee || 0)) : "—"}</strong></div>
            <small>{(watchedApplicants || []).length} applicant{(watchedApplicants || []).length === 1 ? "" : "s"}
              {feePolicy && ` · ${creditApplicantCount} credit check${creditApplicantCount === 1 ? "" : "s"} × ${money(feePolicy.creditCheckFee)}`}</small>
          </div>
          <div className="cc-create-actions">
            <div className={`cc-history-status${historyReady ? " is-complete" : ""}`} aria-live="polite">
              {historyReady ? <><CheckCircleOutlined /> History reviewed</>
                : <><span className="cc-history-step">1 of 2</span> Review applicant history</>}
            </div>
            <div className="cc-create-action-buttons">
              <Button type="text" disabled={creating} onClick={() => setCreateOpen(false)}>Cancel</Button>
              {historyReady && <Button type="text" disabled={creating} onClick={checkHistory}>Review history</Button>}
              <Button type="primary" className="cc-create-primary" loading={creating}
                icon={historyReady ? <SafetyCertificateOutlined /> : <SearchOutlined />}
                disabled={formLoading || (historyReady && unitQuoteLoading)}
                onClick={historyReady ? submitCreate : checkHistory}>
                {historyReady ? "Run Credit Check" : "History Check"}
              </Button>
            </div>
          </div>
        </div>}
      >
        <ConfigProvider theme={{ token: { controlHeight: 36 } }}>
        <Spin spinning={formLoading}>
        <Form form={form} name="creditApplication" layout="vertical" className="cc-create-form" disabled={creating}>
          <div className="cc-create-overview">
            <section className="cc-create-section" aria-labelledby="cc-property-heading">
              <div className="cc-create-section-title"><h3 id="cc-property-heading">Property & application</h3><span>Required fields are marked *</span></div>
              <Row gutter={16}>
                <Col xs={24} sm={12}>
                  <Form.Item name="propId" label="Property" rules={[{ required: true, message: "Choose a property" }]}>
                    <Select showSearch allowClear filterOption={false} onSearch={searchProperties}
                      onChange={(propId) => {
                        form.setFieldValue("unitId", undefined);
                        setUnitOptions([]);
                        searchUnits(propId);
                      }}
                      options={propertyOptions} loading={propertyLoading} placeholder="Search property" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item name="unitId" label="Unit" rules={[{ required: true, message: "Choose a unit" }]}>
                    <Select showSearch filterOption={false} onSearch={(content) => searchUnits(selectedPropId, content)}
                      options={unitOptions} loading={unitLoading} disabled={!selectedPropId || creating}
                      placeholder={selectedPropId ? "Choose a unit" : "Choose property first"} />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}><Form.Item name="orderedBy" label="Ordered by" rules={[{ required: true, message: "Choose the ordering account" }]}>
                  <Select showSearch optionFilterProp="label" options={formOptions.orderedBy} placeholder="Choose an account" />
                </Form.Item></Col>
                <Col xs={24} sm={12}><Form.Item label="Credit check supervisor">
                  <div className="cc-readonly-value">{formOptions.creditCheckSupervisor || "No supervisor configured"}</div>
                </Form.Item></Col>
              </Row>
              <div className="cc-create-toggles">
                <Form.Item name="manager" label="On-site manager" valuePropName="checked"><Switch checkedChildren="Yes" unCheckedChildren="No" /></Form.Item>
                <Form.Item name="pictureDelay" label="Require move-in photos" valuePropName="checked"><Switch checkedChildren="Yes" unCheckedChildren="No" /></Form.Item>
              </div>
            </section>
            <section className="cc-create-section" aria-labelledby="cc-rental-heading">
              <div className="cc-create-section-title"><h3 id="cc-rental-heading">Rent & deposit</h3></div>
              <Row gutter={16}>
                <Col xs={24} sm={12}><Form.Item label="New base rent">
                  <div className="cc-readonly-value cc-readonly-amount">{unitQuoteLoading ? <Spin size="small" /> : unitQuote ? money(unitQuote.rentRate) : "—"}</div>
                </Form.Item></Col>
                <Col xs={24} sm={12}><Form.Item label="Deposit">
                  <div className="cc-readonly-value cc-readonly-amount">{unitQuoteLoading ? <Spin size="small" /> : unitQuote ? money(unitQuote.deposit) : "—"}</div>
                </Form.Item></Col>
                <Col xs={24} sm={12}><Form.Item name="additionalDeposit" label="Deposit add"><InputNumber prefix="$" min={0} max={65000} precision={2} placeholder="0.00" /></Form.Item></Col>
                <Col xs={24} sm={12}><Form.Item name="depositNote" label="Deposit note"><Input placeholder="Optional note" /></Form.Item></Col>
                <Col xs={24} sm={12}><Form.Item name="section8" label="Rental assistance">
                  <Select options={RENTAL_ASSISTANCE_OPTIONS} onChange={() => {
                    form.setFieldsValue({ assistingEntityId: undefined, assistingAmount: undefined });
                  }} />
                </Form.Item></Col>
                {rentalAssistance === "1" && <>
                  <Col xs={24} sm={12}><Form.Item name="assistingEntityId" label="Assisting entity">
                    <Select showSearch allowClear optionFilterProp="label" options={formOptions.assistingEntities} placeholder="Choose an entity" />
                  </Form.Item></Col>
                  <Col xs={24} sm={12}><Form.Item name="assistingAmount" label="Assisting amount" rules={[{ required: true, message: "Enter the assisting amount" }]}>
                    <InputNumber prefix="$" min={0} max={65000} precision={2} />
                  </Form.Item></Col>
                </>}
              </Row>
            </section>
          </div>
          <Form.List name="applicants">
            {(fields, { add, remove }) => (
              <>
                <div className="cc-create-applicants-heading"><h3>Applicants <span>{fields.length}</span></h3></div>
                {fields.map(({ key, name }, index) => {
                  const person = watchedApplicants?.[name];
                  const selectedState = person?.state;
                  return (
                    <section key={key} className="cc-create-person" aria-label={`Applicant ${index + 1}`}>
                      <div className="cc-create-person-header">
                        <div className="cc-create-person-title"><span className="cc-create-person-number">{index + 1}</span><strong>{applicantName(person || {}) || `Applicant ${index + 1}`}</strong></div>
                        <div className="cc-create-person-actions">
                          <Form.Item name={[name, "runCredit"]} label="Run credit check" valuePropName="checked"><Switch checkedChildren="Yes" unCheckedChildren="No" disabled={creating || !can("managerNoCredit")} /></Form.Item>
                          {fields.length > 1 && <Popconfirm title="Remove this applicant?" description="Their entered details will be removed from this application." okText="Remove" okButtonProps={{ danger: true }} onConfirm={() => remove(name)}>
                            <Button type="text" danger disabled={creating}>Remove</Button>
                          </Popconfirm>}
                        </div>
                      </div>
                      <div className="cc-create-person-body">
                        <div className="cc-create-group-label">Personal details</div>
                        <Row gutter={16}>
                          <Col xs={24} sm={12} lg={6}><Form.Item name={[name, "firstName"]} label="First name" rules={[{ required: true, message: "Enter first name" }]}><Input autoComplete="off" /></Form.Item></Col>
                          <Col xs={24} sm={12} lg={6}><Form.Item name={[name, "middleName"]} label="Middle name"><Input /></Form.Item></Col>
                          <Col xs={24} sm={12} lg={6}><Form.Item name={[name, "lastName"]} label="Last name" rules={[{ required: true, message: "Enter last name" }]}><Input /></Form.Item></Col>
                          <Col xs={24} sm={12} lg={6}><Form.Item name={[name, "suffix"]} label="Suffix"><Select allowClear options={SUFFIX_OPTIONS} placeholder="None" /></Form.Item></Col>
                          <Col xs={24} sm={12} lg={6}><Form.Item name={[name, "dateOfBirth"]} label="Date of birth" rules={[{ required: true, message: "Enter date of birth" }]}><DatePicker format="MM/DD/YYYY" placeholder="MM/DD/YYYY" /></Form.Item></Col>
                          <Col xs={24} sm={12} lg={6}><Form.Item name={[name, "socialSecurity"]} label="Social Security number" rules={[{ required: true, message: "Enter Social Security number" }, { pattern: /^(?:\d{9}|\d{3}-\d{2}-\d{4})$/, message: "Enter a nine-digit SSN" }]}><Input.Password autoComplete="off" /></Form.Item></Col>
                          <Col xs={24} sm={12} lg={6}><Form.Item name={[name, "driverLicense"]} label="Driver license"><Input /></Form.Item></Col>
                          <Col xs={24} sm={12} lg={6}><Form.Item name={[name, "driverLicenseState"]} label="License state">
                            <Select showSearch allowClear optionFilterProp="label" options={stateOptions} placeholder="Choose a state" />
                          </Form.Item></Col>
                        </Row>
                        <div className="cc-create-group-label">Contact & income</div>
                        <Row gutter={16}>
                          <Col xs={24} sm={12} lg={6}><Form.Item name={[name, "email"]} label="Email"><Input type="email" /></Form.Item></Col>
                          <Col xs={24} sm={12} lg={6}><Form.Item name={[name, "phone"]} label="Phone"><Input type="tel" /></Form.Item></Col>
                          <Col xs={24} sm={12} lg={6}><Form.Item name={[name, "incomePerMonth"]} label="Income per month"><InputNumber prefix="$" min={0} precision={2} /></Form.Item></Col>
                          <Col xs={24} sm={12} lg={6}><Form.Item name={[name, "lastRentPaid"]} label="Last rent paid"><InputNumber prefix="$" min={0} precision={2} /></Form.Item></Col>
                        </Row>
                        <div className="cc-create-group-label">Current address</div>
                        <Row gutter={16}>
                          <Col xs={24} sm={6} lg={6}><Form.Item name={[name, "streetNumber"]} label="Street number"><Input /></Form.Item></Col>
                          <Col xs={24} sm={12} lg={12}><Form.Item name={[name, "streetName"]} label="Street name"><Input /></Form.Item></Col>
                          <Col xs={24} sm={6} lg={6}><Form.Item name={[name, "unit"]} label="Suite / unit"><Input /></Form.Item></Col>
                          <Col xs={24} sm={6} lg={6}><Form.Item name={[name, "state"]} label="State">
                            <Select showSearch allowClear optionFilterProp="label" options={stateOptions}
                              placeholder="Choose a state" onChange={(state) => {
                                form.setFieldValue(["applicants", name, "city"], undefined);
                                loadCitiesForState(state);
                              }} />
                          </Form.Item></Col>
                          <Col xs={24} sm={12} lg={12}><Form.Item name={[name, "city"]} label="City">
                            <Select showSearch allowClear optionFilterProp="label"
                              options={cityOptionsByState[selectedState] || []} disabled={!selectedState || creating}
                              loading={Boolean(selectedState && !cityOptionsByState[selectedState])}
                              placeholder={selectedState ? "Choose a city" : "Choose state first"}
                              notFoundContent={cityLoadErrors[selectedState]
                                ? <Button type="link" onClick={() => loadCitiesForState(selectedState)}>Retry loading cities</Button>
                                : "No cities maintained for this state"} />
                          </Form.Item></Col>
                          <Col xs={24} sm={6} lg={6}><Form.Item name={[name, "zipCode"]} label="ZIP" rules={[{ required: true, message: "Enter ZIP code" }]}><Input maxLength={5} inputMode="numeric" /></Form.Item></Col>
                        </Row>
                      </div>
                    </section>
                  );
                })}
                <Button type="dashed" className="cc-create-add-person" icon={<PlusOutlined />} block onClick={() => {
                  add(newApplicant());
                  window.requestAnimationFrame(() => form.scrollToField(["applicants", fields.length, "firstName"], { behavior: "smooth", block: "center", focus: true }));
                }}>Add another applicant</Button>
              </>
            )}
          </Form.List>
          {can("upload") && <section className="cc-create-section cc-create-documents">
            <div className="cc-create-section-title"><h3>Application documents</h3><span>Optional</span></div>
            <Upload multiple accept=".pdf,.png,.jpg,.gif" fileList={createFiles}
              beforeUpload={(file) => {
                if (!/\.(pdf|png|jpg|gif)$/i.test(file.name)) {
                  message.warning("Choose a PDF, PNG, JPG or GIF file");
                  return Upload.LIST_IGNORE;
                }
                return false;
              }}
              onChange={({ fileList }) => setCreateFiles(fileList)}>
              <Button icon={<UploadOutlined />}>Choose documents</Button>
            </Upload>
            <p className="cc-history-description">Application forms and supporting documents. Selected files upload after the application is submitted.</p>
          </section>}
        </Form>
        </Spin>
        </ConfigProvider>
      </Modal>

      {historySsns && <HistoryCheckModal socialSecurityNumbers={historySsns} onClose={() => setHistorySsns(null)}
        onReviewed={() => {
          if (historySnapshot.current === historyIdentity(form.getFieldValue("applicants"))) {
            reviewedHistoryRef.current = historySnapshot.current;
            setReviewedHistory(historySnapshot.current);
          } else {
            message.warning("Applicant details changed. Please check history again.");
          }
          setHistorySsns(null);
        }} />}

      <Modal
        open={Boolean(detail) || openingDetailId !== null}
        className="cc-detail-modal"
        onCancel={closeDetail}
        closable={!busy && !uploadType}
        keyboard={!busy && !uploadType}
        maskClosable={false}
        width={900}
        centered
        destroyOnClose
        footer={<Space>
          <Button disabled={busy || Boolean(uploadType)} onClick={closeDetail}>Close</Button>
          {detail && !detail.movedInStatus && can("update") && <Button disabled={busy || Boolean(uploadType) || detailLoading}
            loading={changingUnitId === detail.applicationId} onClick={() => openChangeUnit(detail.applicationId)}>Change unit</Button>}
          {detail?.movedInStatus && can("transferTenant") ? <Button type="primary" disabled={busy || Boolean(uploadType) || detailLoading}
            onClick={() => setTransferApplication(detail)}>Transfer tenant</Button> : null}
          {detail && !detail.movedInStatus && can("moveIn") && <Button type="primary"
            disabled={!ready || busy || Boolean(uploadType) || detailLoading} loading={moveInTargetId === detail.applicationId}
            onClick={() => openMoveIn(detail.applicationId)}>Move in</Button>}
        </Space>}
        title={detail ? <div className="cc-detail-title">
          <div><div className="cc-eyebrow">APPLICATION #{detail.applicationId}</div><strong>{detail.prop} · {detail.unit}</strong></div>
          <Button size="small" icon={<ReloadOutlined />} loading={detailLoading} disabled={busy || Boolean(uploadType)}
            onClick={() => loadDetail(detail.applicationId)}>Refresh</Button>
        </div> : `Application #${openingDetailId}`}
      >
        <Spin spinning={detailLoading}>
        {!detail && <div className="cc-loading-panel" aria-label="Loading application" />}
        {detail && <>
          {unknown && <Alert type="warning" showIcon className="cc-inline-alert"
            message="The external submission outcome needs review"
            description="One or more credit submissions have an unknown outcome. Do not submit them again until the provider confirms whether they were charged." />}
          <div className="cc-detail-hero">
            <div><Text type="secondary">Application</Text><div>{badge(detail.applicationStatus)}</div></div>
            <div><Text type="secondary">Supervisor</Text><div>{badge(detail.supervisorStatus)}</div></div>
            <div><Text type="secondary">Payment</Text><div><Tag color={detail.paymentStatus === 101 ? "green" : "gold"}>{paymentName(detail.paymentStatus)}</Tag></div></div>
          </div>
          <Descriptions size="small" column={2} className="cc-descriptions">
            <Descriptions.Item label="Expected fee">{money(detail.expectedAmount)}</Descriptions.Item>
            <Descriptions.Item label="Received">{money(detail.receivedAmount)}</Descriptions.Item>
            <Descriptions.Item label="Base rent">{money(detail.newRent)}</Descriptions.Item>
            <Descriptions.Item label="Deposit">{money(Number(detail.deposit || 0) + Number(detail.additionalDeposit || 0))}</Descriptions.Item>
            {detail.depositNote && <Descriptions.Item label="Deposit note">{detail.depositNote}</Descriptions.Item>}
            <Descriptions.Item label="Ordered by">{detail.orderedByName || "—"}</Descriptions.Item>
            <Descriptions.Item label="Rental assistance">{detail.section8 === "1" ? "Yes" : detail.section8 === "0" ? "No" : "—"}</Descriptions.Item>
            {detail.section8 === "1" && <Descriptions.Item label="Tenant assisting entity">{detail.assistingEntityName || "—"}</Descriptions.Item>}
            {detail.section8 === "1" && detail.assistingAmount != null &&
              <Descriptions.Item label="Assisting amount">{money(detail.assistingAmount)}</Descriptions.Item>}
            <Descriptions.Item label="Manager">{detail.manager ? "Yes" : "No"}</Descriptions.Item>
            <Descriptions.Item label="Move-in date">{detail.movedInStatus ? displayDate(detail.moveInDate) : "Not moved in"}</Descriptions.Item>
          </Descriptions>
          <div className="cc-form-section-heading">Applicants</div>
          <div className="cc-person-list">
            {(detail.applicants || []).map((person) => (
              <div className="cc-person" key={person.applicationInfoId}>
                <div className="cc-avatar">{person.firstName?.[0]}{person.lastName?.[0]}</div>
                <div><strong>{person.firstName} {person.lastName}{person.suffix ? ` ${person.suffix}` : ""}</strong>
                  <small>SSN ending {ssnLast4(person.ssnLast4)} · {person.runCredit === "0" ? "Credit skipped" : "Credit requested"}</small>
                  {(person.incomePerMonth != null || person.lastRentPaid != null) &&
                    <small>Monthly income {person.incomePerMonth == null ? "—" : money(person.incomePerMonth)} · Last rent paid {person.lastRentPaid == null ? "—" : money(person.lastRentPaid)}</small>}
                </div>
                <div className="cc-person-score">{person.score == null ? "—" : person.score}<small>Score</small>
                  {person.reportAvailable === 1 && <Button type="link" size="small" onClick={() => showReport(person)}>View report</Button>}
                </div>
              </div>
            ))}
          </div>
          <div className="cc-form-section-heading">Move-in readiness</div>
          {!detail.movedInStatus && detail.priorEviction && <Alert type="error" showIcon className="cc-inline-alert"
            message="Prior eviction record found"
            description={can("evictionOverride") ? "Your Credit Check menu permission allows an authorized override. Review the record before proceeding." : "An authorized reviewer must complete this move-in."} />}
          {detail.movedInStatus ? <Alert type="success" showIcon message="Move-in completed" />
            : ready ? <Alert type="success" showIcon message="Ready for move-in" description="Required approvals, payment, and documents are complete." />
            : <Alert type="warning" showIcon message="More steps are needed" description={
              <ul className="cc-reasons">
                {(detail.moveInEligibility?.reasons || []).map((reason) => <li key={reason}>{reason}</li>)}
                {detail.priorEviction && !can("evictionOverride") && <li>Prior eviction requires authorized review</li>}
              </ul>
            } />}
          <div className="cc-form-section-heading">Documents</div>
          {pendingUploads.some((item) => item.applicationId === detail.applicationId) && <Alert
            className="cc-inline-alert" showIcon type="warning" message="Application saved; documents pending upload"
            description={pendingUploads.filter((item) => item.applicationId === detail.applicationId).map((item) => item.file.name).join(", ")}
            action={<Button size="small" loading={uploadingApplicationId === detail.applicationId}
              disabled={uploadingApplicationId !== null} onClick={() => uploadApplicationFiles(detail.applicationId,
                pendingUploads.filter((item) => item.applicationId === detail.applicationId).map((item) => item.file))}>Retry upload</Button>} />}
          <div className="cc-file-list">
            {(detail.files || []).map((file) => (
              <div className="cc-file" key={file.fileId}>
                <FileAddOutlined />
                <Button type="link" loading={openingFileId === file.fileId} disabled={openingFileId !== null}
                  onClick={() => showFile(file)}>{file.name || file.file}</Button>
                <Tag>{file.type.replace("_", " ")}</Tag>
                {can("upload") && <Popconfirm title="Remove this file?" onConfirm={() =>
                  updateDetail(api.creditCheckDeactivateFile, { fileId: file.fileId }, "File removed")}>
                  <Button type="link" danger disabled={busy || Boolean(uploadType)}>Remove</Button>
                </Popconfirm>}
              </div>
            ))}
            {!detail.files?.length && <Text type="secondary">No files uploaded yet.</Text>}
          </div>
          {can("upload") && <Space wrap className="cc-upload-actions">
            {[["application", "Application"], ["agreement", "Agreement PDF"], ["movein_image", "Move-in photo"]].map(([type, label]) =>
              <Upload key={type} accept={type === "application" ? ".pdf,.png,.jpg,.gif" : type === "agreement" ? ".pdf" : ".jpg,.jpeg,.png,.webp,.pdf"}
                disabled={Boolean(uploadType) || busy} showUploadList={false} customRequest={({ file, ...callbacks }) => uploadFile(file, type, callbacks)}>
                <Button icon={<UploadOutlined />} disabled={Boolean(uploadType) || busy} loading={uploadType === type}>{label}</Button>
              </Upload>
            )}
          </Space>}
          <Timeline className="cc-timeline" items={[
            { color: "green", children: "Application created" },
            { color: detail.applicationStatus === "Pending" ? "gray" : "green", children: `Credit result: ${detail.applicationStatus}` },
            { color: detail.supervisorStatus === "Approved" ? "green" : "gray", children: `Supervisor: ${detail.supervisorStatus}` },
            { color: detail.movedInStatus ? "green" : "gray", children: detail.movedInStatus ? "Moved in" : "Move-in pending" },
          ]} />
        </>}
        </Spin>
      </Modal>
      <Modal open={Boolean(adjustTarget)} className="cc-adjust-modal"
        title={adjustTarget && `Adjust received amount · #${adjustTarget.applicationId}`}
        onCancel={() => setAdjustTarget(null)}
        closable={!busy} keyboard={!busy} maskClosable={false} cancelButtonProps={{ disabled: busy }}
        onOk={async () => {
          if (await updateApplication(adjustTarget.applicationId, api.creditCheckAdjustReceived,
            { amount: String(amount) }, "Received amount adjusted")) setAdjustTarget(null);
        }}
        okButtonProps={{ loading: busy, disabled: amount === null }}>
        <Alert type="info" showIcon message="The amount can only be reduced" className="cc-inline-alert" />
        <div className="cc-adjust-context">
          <span>Expected <strong>{money(adjustTarget?.expectedAmount)}</strong></span>
          <span>Currently received <strong>{money(adjustTarget?.receivedAmount)}</strong></span>
        </div>
        <div className="cc-adjust-label">New received amount</div>
        <InputNumber min={0} max={Number(adjustTarget?.receivedAmount || 0)} precision={2} value={amount}
          onChange={setAmount} style={{ width: "100%" }} />
      </Modal>
      <Modal open={Boolean(report)} className="cc-report-modal" title={report && `Credit report · ${report.name}`}
        width={900} footer={<Button onClick={closeReport}>Close</Button>}
        onCancel={closeReport} destroyOnClose>
        <Spin spinning={Boolean(report) && report.xml === null}>
          {report?.xml ? <pre className="cc-report-xml">{formatReportXml(report.xml)}</pre> : <div className="cc-loading-panel" />}
        </Spin>
      </Modal>
      <MoveInModal open={moveInOpen} application={moveInApplication}
        onClose={() => { setMoveInOpen(false); setMoveInApplication(null); }}
        onSaved={() => { setMoveInOpen(false); setMoveInApplication(null); refresh(); }} />
      {transferApplication && <TransferTenantModal application={transferApplication}
        onClose={() => setTransferApplication(null)} onSaved={() => { setTransferApplication(null); refresh(); }} />}
      {changeUnitApplication && <ChangeUnitModal application={changeUnitApplication}
        onClose={() => setChangeUnitApplication(null)} onSaved={() => { setChangeUnitApplication(null); refresh(); }} />}
    </div>
    </ConfigProvider>
  );
}
